import * as Core from '@skylab-shared/core';
import { PropertyChangedJoinPoint, QueryBus, RepositoryResponseConflictCodes, ResponseConflictObject } from '@skylab-shared/core';
import { NumberingModel } from '@skylab-shared/dataModel';
import { memoize, not, sameValues } from '@skylab-shared/utils';
import { ContextKeys } from '../common/context-keys';
import { propertyChangedJoinPointFilter } from '../common/property-changed/property-changed-filter';
import { NumberingToken, tokenizeNumberingFormat } from './numbering-format-lexer';
import { doDateTokensRescpectAsteriskRule, hasAnyDateTokenAsterisk, isAnyDateTokenInTokens, isAnyNotAllowedTagUsed, isNumberTokenInTokens } from './numbering-format-validators';
import { NumberingLinkedWithSalePatternQuery } from './queries/numbering-linked-with-sale-pattern.query-base';
import { AnyDateTagHasNotAsteriskError } from './violations/any-date-tag-has-not-asterisk-error';
import { DateTagNotUsedError } from './violations/date-tag-not-used-error';
import { NotAllowedTagError } from './violations/not-allowed-tag-error';
import { NumberTagNotUsedError } from './violations/number-tag-not-used-error';
import { NumberingFormatDoesNotRespectAsteriskRuleError } from './violations/numbering-format-does-not-respect-asterisk-rule-error';
import { NumberingLinkedWithSalePatternError } from './violations/numbering-linked-with-sale-pattern-error';

@Core.BO({ modelType: NumberingModel })
export class NumberingBO extends Core.BusinessObjectBase<NumberingModel> {
    private previousNumberingActiveValue: boolean;
    private previousNumberingDefaultValue: boolean;

    public constructor(
        weaver: Core.Weaver,
        private readonly queryBus: QueryBus
    ) {
        super(weaver);
    }

    protected override async initializeAsync(): Promise<void> {
        await super.initializeAsync();

        this.registerValidationRules();
        this.registerReadonlyRules();

        if (this.model.isPersistent) {
            this.previousNumberingActiveValue = this.model.active;
            this.previousNumberingDefaultValue = this.model.default;
        }
    }

    protected override handleConflictSaveResponse(conflict: ResponseConflictObject<RepositoryResponseConflictCodes>) {
        if ((conflict.code === RepositoryResponseConflictCodes.UniqueConstraintViolation) && sameValues(conflict.objectProperties!, ['name'])) {
            this.addExternalRuleViolations({
                [this.model.sun]: [{
                    property: 'name',
                    violation: new Core.NonUniqueModelDataError('business.numberings.validation.name.server-rule-violations.non-unique-model-data-error'),
                }]
            });
        }
    }

    private registerNumberingActiveValidationRule(): void {
        this.validation.addRule(
            [[NumberingModel, 'active']],
            async _ => {
                if ((this.previousNumberingActiveValue === true) && (this.model.active === false)) {
                    return !(await this.isNumberingLinkedWithSalePattern());
                }
                return true;
            },
            NumberingLinkedWithSalePatternError
        );
    }

    public async isNumberingLinkedWithSalePattern(): Promise<boolean> {
        const linkedDocuments = await this.localContext
            .getAsync(ContextKeys.numberingWithLinkedSalePattern(this.model.id),
            () => this.queryBus.queryAsync(new NumberingLinkedWithSalePatternQuery(this.model.id)));

        return linkedDocuments.some(elem => elem.status === 'Active');
    }

    private registerValidationRules(): void {
        this.registerListening(this.weaver.register(PropertyChangedJoinPoint, propertyChangedJoinPointFilter(this)(NumberingModel, ['default'])(
            _ => {
                if (this.model.default === true) {
                    this.model.active = true;
                }
            }
        )));

        this.validation.addRule(
            [[NumberingModel, 'format'], [NumberingModel, 'manual']], [[NumberingModel, 'format']],
            ctx => validateFormatWith(isNumberTokenInTokens, ctx.source),
            NumberTagNotUsedError
        );

        this.validation.addRule(
            [[NumberingModel, 'format'], [NumberingModel, 'manual']], [[NumberingModel, 'format']],
            ctx => validateFormatWith(isAnyDateTokenInTokens, ctx.source),
            DateTagNotUsedError
        );

        this.validation.addRule(
            [[NumberingModel, 'format'], [NumberingModel, 'manual']], [[NumberingModel, 'format']],
            ctx => validateFormatWith(hasAnyDateTokenAsterisk, ctx.source),
            AnyDateTagHasNotAsteriskError
        );

        this.validation.addRule(
            [[NumberingModel, 'format'], [NumberingModel, 'manual']], [[NumberingModel, 'format']],
            ctx => validateFormatWith(doDateTokensRescpectAsteriskRule, ctx.source),
            NumberingFormatDoesNotRespectAsteriskRuleError
        );

        this.validation.addRule(
            [[NumberingModel, 'format'], [NumberingModel, 'manual']], [[NumberingModel, 'format']],
            ctx => validateFormatWith(tokens => not(isAnyNotAllowedTagUsed(tokens)), ctx.source),
            NotAllowedTagError
        );

        const tokenizeNumberingFormatWithCache = memoize(tokenizeNumberingFormat);
        const validateFormatWith = (validateFn: (tokens: NumberingToken[]) => boolean, model: NumberingModel) => {
            if (model.manual)
                return true;
                if (model.format == null)
                return false;
            return validateFn(tokenizeNumberingFormatWithCache(model.format));
        };

        this.registerNumberingActiveValidationRule();
    }

    private registerReadonlyRules(): void {
        this.readonly.addRule([
            [NumberingModel, 'startingNumber'],
            [NumberingModel, 'additionalSymbol'],
            [NumberingModel, 'format']],
            ctx => ctx.source.documentNumberInUse
        );

        this.readonly.addRule(
            [[NumberingModel, 'manual']],
            [[NumberingModel, 'startingNumber'],
            [NumberingModel, 'gapFillingMethod'],
            [NumberingModel, 'additionalSymbol'],
            [NumberingModel, 'format']],
            ctx => ctx.source.manual
        );

        this.readonly.addRule(
            [[NumberingModel, 'default']],
            _ => this.previousNumberingDefaultValue
        );

        this.readonly.addRule(
            [[NumberingModel, 'default']],
            [[NumberingModel, 'active']],
            ctx => ctx.source.default
        );

        this.readonly.addRule(
            [[NumberingModel, 'kinds']],
            ctx => ctx.source.isPersistent
        );

        this.readonlyChecker.addOmitRule([
            [NumberingModel, 'format'],
        ]);
    }
}
